import { Icon } from '@material-ui/core';
import { ConfigProvider, Select } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { getAppConfig } from '../services/configService';
import CookieService from '../services/CookieService';
import preloadManager, { preloadTasks } from '../utils/preloader';
import { redirectToCustomerPortal } from '../utils/portalUrl';
import { useAppConfig } from './AppConfigProvider';
import AuthenticationModal from './AuthenticationModal';

// Theme will be dynamically generated based on config
const TopBarContainer = styled.div`
  display: flex;
  width: 100%;
`;

const TopBarContent = styled.div`
  background: ${props => props.theme.primary};
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`;

// Logo 和品牌部分
const LogoContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 260px;
`;

const TopBarLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
`;

const AppName = styled.div`
  color: #fdf8ff;
  text-align: left;
  font-size: 18px;
  line-height: 14px;
  letter-spacing: 0.1px;
  font-weight: 700;
  font-style: italic;
`;

// 标签页部分
const Tabs = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
`;

const Tab = styled.div`
  padding: 8px 12px;
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: center;
  justify-content: flex-start;
  height: 100%;
  background: ${props => props.selected ? props.theme.inversePrimary : 'transparent'};
`;

const TabLabel = styled.div`
  color: ${props => props.selected ? props.theme.inverseSurface : props.theme.textOnPrimary};
  text-align: left;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.1px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const VerticalDivider = styled.div`
  width: 1px;
  height: 32px;
  background-color: rgba(255, 255, 255, 0.3);
`;

// 动作区域
const ActionsContainer = styled.div`
  padding: 0 16px;
  display: flex;
  flex-direction: row;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
`;

const LanguageSelect = styled(Select)`
  width: 120px;
  .ant-select-selector {
     border-radius: 0px;
    display: flex;
    align-items: center;
  }
`;

const UserAvatar = styled.div`
  background: ${props => props.theme.inversePrimary};
  border-radius: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  position: relative;
  cursor: pointer;
  
  &:hover {
    background: ${props => `${props.theme.inversePrimary}CC`};
  }
`;

const AvatarText = styled.div`
  color: ${props => props.theme.inverseSurface};
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.5px;
  font-weight: 400;
`;

// User dropdown menu
const UserDropdown = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  width: 300px;
  background: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  z-index: 1000;
  display: ${props => props.isOpen ? 'block' : 'none'};
  margin-top: 4px;
`;

const UserMenuContainer = styled.div`
  position: relative;
`;

const UserInfoSection = styled.div`
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
`;

const UserName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`;

const UserEmail = styled.div`
  font-size: 14px;
  color: #666;
`;

const UserAccountInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
`;

const AccountAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${props => props.theme.primary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const AccountAvatarText = styled.div`
  color: white;
  font-size: 20px;
  font-weight: 500;
`;

const AccountDetails = styled.div`
  flex: 1;
`;

const LogoutButton = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 8px 16px;
  border-top: 1px solid #f0f0f0;
`;

const LogoutButtonText = styled.div`
  color: ${props => props.theme.primary};
  font-size: 14px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 4px;
  
  &:hover {
    background: ${props => props.theme.primary}10;
  }
`;

const MenuSection = styled.div`
  padding: 8px 0;
`;

const MenuItem = styled.div`
  padding: 8px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  
  &:hover {
    background: ${props => `${props.theme.primary}10`};
  }
`;

const MenuItemLabel = styled.div`
  font-size: 14px;
  color: #333;
`;

const AccountId = styled.div`
  font-size: 12px;
  color: #888;
`;

const TenantInfo = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 4px;
`;

const TenantName = styled.div`
  font-size: 13px;
  color: #666;
  font-weight: 500;
`;

const VerificationTag = styled.div`
  font-size: 12px;
  color: ${props => props.theme.primary};
  padding: 6px 0px;
  border-radius: 4px;
`;

// 图标容器 - 保持原有的类名以避免影响其他组件
const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

// 新增图标样式
const StyledIconWrapper = styled.div`
  .icon-primary {
    color: ${props => props.theme.inverseSurface};
  }
  
  .icon-onprimary {
    color: ${props => props.theme.textOnPrimary};
  }
  
  .icon-selected {
    color: ${props => props.theme.inverseSurface};
  }
`;

const TopBar = () => {
  const { config, loading, theme } = useAppConfig();
  const [userInfo, setUserInfo] = useState(null);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({ taxNo: '', taxAgencyAccount: '' });
  const dropdownRef = useRef(null);
  const { t, i18n } = useTranslation();

  // 获取应用程序和租户信息
  const themeSettings = config?.settings?.themeSetting;
  const showManagementMenu = config?.settings?.uiSettings?.showManagementMenu === true;
  const logoUrl = themeSettings?.logoUrl || "";
  const appName = config?.application?.name || t('app.name');
  const tenantName = config?.tenant?.name || "SIMALFA";

  useEffect(() => {
    // Get user info from CookieService instead of localStorage
    try {
      const userInfo = CookieService.getUserInfo();
      if (userInfo) {
        setUserInfo(userInfo);
      }
    } catch (error) {
      console.error('Error getting userInfo from CookieService:', error);
    }

    // Get saved language from localStorage
    const savedLanguage = localStorage.getItem('userLanguage');
    if (savedLanguage) {
      setCurrentLanguage(savedLanguage);
      i18n.changeLanguage(savedLanguage);
    }

    // Fetch company info for authentication
    const fetchCompanyInfo = async () => {
      try {
        const appConfig = await getAppConfig('einvoice', 'standalone');
        const settings = appConfig?.settings || {};
        if (settings.companyInfo) {
          setCompanyInfo({
            taxNo: settings.companyInfo.taxNo || '',
            taxAgencyAccount: settings.companyInfo.taxAgencyAccount || ''
          });
        }
      } catch (error) {
        console.error('Error fetching company info:', error);
      }
    };
    fetchCompanyInfo();

    // Add click outside listener to close dropdown
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    // 智能预加载管理页面 - 网络条件允许的情况下在空闲时预加载
    preloadManager.networkAwarePreload(() => {
      preloadTasks.allManagementPages();
    });

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [i18n]);

  // 预加载处理函数
  const handleManagementHover = () => {
    // 当用户hover在管理菜单时立即预加载
    preloadTasks.managementPage();
  };

  const toggleUserDropdown = () => {
    setUserDropdownOpen(!userDropdownOpen);

    // 当用户打开下拉菜单时，预加载管理页面
    if (!userDropdownOpen) {
      preloadManager.smartPreload('idle', () => {
        preloadTasks.allManagementPages();
      }, 200);
    }
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!userInfo || !userInfo.name) return 'U';
    return userInfo.name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  if (loading) {
    return <div>{t('common.loading')}</div>;
  }

  const handleLogout = () => {
    CookieService.logout();
    redirectToCustomerPortal({ returnUrl: window.location.href });
  };

  const handleLanguageChange = (value) => {
    setCurrentLanguage(value);
    i18n.changeLanguage(value);
    localStorage.setItem('userLanguage', value);
  };

  return (
    <ConfigProvider theme={{ token: { colorPrimary: theme.primary } }}>

      <StyledIconWrapper theme={theme}>
        <TopBarContainer>
          <TopBarContent theme={theme}>
            <LogoContainer>
              <TopBarLogo>
                <img height={40} src={`/api/${logoUrl}`} alt="logo" />
              </TopBarLogo>
              {/* <AppName>{tenantName}</AppName> */}
            </LogoContainer>

            <Tabs>
              <Tab selected theme={theme}>
                <IconContainer className="icon">
                  <Icon className="icon-selected icon-medium">receipt_long</Icon>
                </IconContainer>
                <TabLabel selected theme={theme}>{t('menu.einvoice')}</TabLabel>
              </Tab>
            </Tabs>

            <ActionsContainer>
              <LanguageSelect
                defaultValue={currentLanguage}
                value={currentLanguage}
                onChange={handleLanguageChange}
                options={[
                  { value: 'en', label: 'English' },
                  { value: 'zh', label: '中文' },
                ]}
                dropdownStyle={{ zIndex: 1001 }}
              />

              <UserMenuContainer ref={dropdownRef}>
                <UserAvatar theme={theme} onClick={toggleUserDropdown}>
                  <AvatarText theme={theme}>{getUserInitials()}</AvatarText>
                </UserAvatar>

                <UserDropdown isOpen={userDropdownOpen}>
                  <UserAccountInfo>
                    <AccountAvatar theme={theme}>
                      <AccountAvatarText>{getUserInitials()}</AccountAvatarText>
                    </AccountAvatar>
                    <AccountDetails>
                      <UserName>{userInfo?.name || 'User'}</UserName>
                      <AccountId>{t('user.accountId')}: {userInfo?.id?.substring(0, 16) || '-'}</AccountId>
                      <TenantInfo>
                        <TenantName>{userInfo?.tenantName || userInfo?.tenant?.name || '-'}</TenantName>
                        <VerificationTag theme={theme}>{t('user.enterpriseVerification')}</VerificationTag>
                      </TenantInfo>
                    </AccountDetails>
                  </UserAccountInfo>

                  <MenuSection>
                    <MenuItem >
                      <IconContainer className="icon">
                        <Icon className="icon-grey icon-medium">help_outline</Icon>
                      </IconContainer>
                      <MenuItemLabel>{t('menu.help')}</MenuItemLabel>
                    </MenuItem>

                    {/* Management menu item - visible when uiSettings.showManagementMenu is enabled */}
                    {showManagementMenu && (
                      <MenuItem
                        onClick={() => { }}
                        onMouseEnter={handleManagementHover}
                        style={{ padding: 0 }}
                      >
                        <Link to="/management" style={{
                          display: 'flex',
                          width: '100%',
                          height: '100%',
                          padding: '8px 16px',
                          textDecoration: 'none',
                          color: 'inherit',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <IconContainer className="icon">
                            <Icon className="icon-grey icon-medium">receipt</Icon>
                          </IconContainer>
                          <MenuItemLabel>{t('menu.management')}</MenuItemLabel>
                        </Link>
                      </MenuItem>
                    )}

                    {/* Authentication menu item */}
                    <MenuItem onClick={() => setAuthModalOpen(true)}>
                      <IconContainer className="icon">
                        <Icon className="icon-grey icon-medium">security</Icon>
                      </IconContainer>
                      <MenuItemLabel>{t('settings.authentication')}</MenuItemLabel>
                    </MenuItem>

                    <MenuItem onClick={handleLogout}>
                      <IconContainer className="icon">
                        <Icon className="icon-grey icon-medium">logout</Icon>
                      </IconContainer>
                      <MenuItemLabel>{t('menu.signOut')}</MenuItemLabel>
                    </MenuItem>
                  </MenuSection>
                </UserDropdown>
              </UserMenuContainer>
            </ActionsContainer>
          </TopBarContent>
        </TopBarContainer>
      </StyledIconWrapper>

      {/* Authentication Modal */}
      <AuthenticationModal
        open={authModalOpen}
        onCancel={() => setAuthModalOpen(false)}
        taxNo={companyInfo.taxNo}
        taxAgencyAccount={companyInfo.taxAgencyAccount}
      />
    </ConfigProvider>
  );
};

export default TopBar; 